/**
 * Pesan galat berbahasa manusia + apakah mengulang masuk akal.
 *
 * `canRetry` bukan hiasan. Menampilkan tombol "Coba lagi" pada kegagalan yang tidak akan pernah
 * berhasil membuat pengguna lapangan menekannya berulang kali di bawah matahari, alih-alih
 * melakukan hal yang sebenarnya memperbaiki keadaan (pindah tempat, hubungi admin).
 *
 * ⚠️ CATATAN LAPANGAN — kode `ATT-001`…`ATT-010` saat ini TIDAK PERNAH sampai ke klien.
 * Seluruhnya tidak terdaftar di `DomainExceptionFilter` sehingga keluar sebagai `500 INTERNAL`
 * berpesan "Terjadi kesalahan internal." (terbukti terhadap `main` c58285e — lihat Issue #479).
 * Pemetaan di bawah tetap ditulis lengkap supaya begitu #479 merged, layar ini langsung benar
 * tanpa perubahan. Sampai saat itu, penolakan kebijakan jatuh ke cabang 500 di `fallbackFor()`.
 */

export interface ErrorPresentation {
  readonly title: string;
  readonly hint: string;
  readonly canRetry: boolean;
  /** Field yang harus diperbaiki pengguna, bila galat menunjuk salah satu. */
  readonly focusField?: 'outsideRadiusReason' | 'selfie';
}

const BY_CODE: Readonly<Record<string, ErrorPresentation>> = {
  'ATT-001': {
    title: 'Kebijakan absensi belum disiapkan',
    hint: 'Admin organisasi Anda belum mengaktifkan kebijakan absensi. Hubungi admin.',
    canRetry: false,
  },
  'ATT-002': {
    title: 'Masih ada sesi terbuka',
    hint: 'Anda sudah absen masuk dan belum absen pulang. Lakukan absen pulang lebih dulu.',
    canRetry: false,
  },
  'ATT-003': {
    title: 'Belum ada absen masuk aktif',
    hint: 'Absen pulang hanya bisa dilakukan setelah absen masuk.',
    canRetry: false,
  },
  'ATT-004': {
    title: 'Di luar radius lokasi kerja',
    hint: 'Kebijakan organisasi tidak mengizinkan absen masuk di luar radius. Datang lebih dekat ke lokasi kerja, atau ajukan koreksi.',
    canRetry: false,
  },
  'ATT-005': {
    title: 'Akurasi GPS belum memadai',
    hint: 'Keluar ke area terbuka atau tunggu sinyal membaik, lalu coba lagi.',
    canRetry: true,
  },
  'ATT-006': {
    title: 'Swafoto wajib disertakan',
    hint: 'Kebijakan organisasi mewajibkan swafoto saat absen masuk.',
    canRetry: false,
    focusField: 'selfie',
  },
  'ATT-007': {
    title: 'Alasan wajib diisi',
    hint: 'Anda di luar radius lokasi kerja. Tuliskan alasannya untuk melanjutkan.',
    canRetry: false,
    focusField: 'outsideRadiusReason',
  },
  'ATT-008': {
    title: 'Pengajuan sudah diputuskan',
    hint: 'Seseorang sudah menyetujui atau menolaknya lebih dulu. Muat ulang antrean untuk melihat keputusan terakhir.',
    canRetry: false,
  },
  'ATT-009': {
    title: 'Tidak boleh memutuskan pengajuan ini',
    hint: 'Pengaju dan orang yang absensinya dikoreksi tidak boleh menjadi penilai. Minta manajer lain yang memutuskan.',
    canRetry: false,
  },
  'ATT-010': {
    title: 'Tanggal sudah lewat',
    hint: 'Absen masuk untuk tanggal yang sudah lewat harus lewat jalur koreksi absensi.',
    canRetry: false,
  },
  'ATT-011': {
    title: 'Rentang tanggal tidak valid',
    hint: 'Persempit rentang tanggal, maksimum 31 hari.',
    canRetry: false,
  },
  'WF-403': {
    title: 'Bukan anggota organisasi',
    hint: 'Akun Anda tidak terdaftar sebagai anggota organisasi ini. Hubungi admin.',
    canRetry: false,
  },
  'WF-404': {
    title: 'Belum punya peran Workforce',
    hint: 'Akun Anda belum diberi peran di SalesHub Pro Workforce. Hubungi admin organisasi.',
    canRetry: false,
  },
  'WF-405': {
    title: 'Tidak berhak',
    hint: 'Peran Anda tidak mencakup tindakan ini. Hubungi admin organisasi.',
    canRetry: false,
  },
  'WF-406': {
    title: 'Di luar cakupan Anda',
    hint: 'Data ini berada di luar tim atau organisasi yang boleh Anda akses.',
    canRetry: false,
  },
  // Layanan keanggotaan tidak dapat dihubungi. Server memilih 503, BUKAN 403 — ia tidak tahu
  // apakah Anda anggota. Klien harus jujur menyampaikan ketidaktahuan itu, bukan menampilkan
  // data lama seolah normal.
  'WF-503': {
    title: 'Keanggotaan tidak dapat diverifikasi',
    hint: 'Layanan keanggotaan organisasi sedang tidak dapat dihubungi. Coba beberapa saat lagi.',
    canRetry: true,
  },
  'WF-900': {
    title: 'Permintaan tidak lengkap',
    hint: 'Kunci idempotensi tidak sah. Muat ulang halaman lalu ulangi.',
    canRetry: true,
  },
  'WF-902': {
    title: 'Permintaan berbeda dengan sebelumnya',
    hint: 'Tindakan sebelumnya dengan kunci yang sama berbeda isinya. Muat ulang halaman.',
    canRetry: false,
  },
  'WF-903': {
    title: 'Permintaan bersamaan belum selesai',
    hint: 'Permintaan kembar sedang diproses. Tunggu sebentar lalu periksa status Anda.',
    canRetry: true,
  },
  // ── Field Task (Gate D, ADR-0035 §5) ──────────────────────────────────────
  'TASK-001': {
    title: 'Tugas tidak ditemukan',
    hint: 'Tugas mungkin sudah dihapus atau bukan milik organisasi Anda. Kembali ke daftar tugas.',
    canRetry: false,
  },
  'TASK-002': {
    title: 'Tindakan tidak sah pada status ini',
    hint: 'Status tugas sudah berubah — mungkin orang lain memperbaruinya. Muat ulang untuk melihat keadaan terkini.',
    canRetry: false,
  },
  'TASK-003': {
    title: 'Tugas ini bukan milik Anda',
    hint: 'Hanya pelaksana yang ditugaskan yang boleh menjalankannya. Bila seharusnya milik Anda, hubungi dispatcher.',
    canRetry: false,
  },
  'TASK-004': {
    title: 'Pelaksana tidak aktif',
    hint: 'Orang yang dipilih bukan anggota aktif organisasi ini. Pilih pelaksana lain.',
    canRetry: false,
  },
  'TASK-005': {
    title: 'Jenis tugas tidak dikenal',
    hint: 'Jenis tugas tidak tersedia atau sudah dinonaktifkan. Muat ulang daftar jenis tugas.',
    canRetry: false,
  },
  'TASK-006': {
    title: 'Bukti pengiriman belum lengkap',
    hint: 'Tugas ini tidak dapat diselesaikan tanpa bukti terverifikasi. Pastikan foto dan tanda tangan sudah terunggah, lalu ulangi.',
    canRetry: false,
  },
  'TASK-007': {
    title: 'Alasan kegagalan belum lengkap',
    hint: 'Alasan yang Anda pilih menuntut keterangan tambahan. Jelaskan apa yang terjadi di lokasi.',
    canRetry: false,
  },
  'TASK-008': {
    title: 'Tugas sudah berjalan — tidak bisa dialihkan',
    hint: 'Pengalihan hanya bisa sebelum pelaksana mulai bekerja. Batalkan tugas ini lalu buat yang baru bila perlu.',
    canRetry: false,
  },
  'TASK-009': {
    title: 'Detail pengiriman belum lengkap',
    hint: 'Tugas pengiriman menuntut daftar barang dan penerima. Layar pengiriman menyusul; untuk sekarang pakai jenis tugas lain.',
    canRetry: false,
  },
  'TASK-010': {
    title: 'Kunjungan sales bukan milik Workforce',
    hint: 'Kunjungan sales dikelola CRM dan tidak dapat dibuat dari sini. Buat tugas dengan jenis lain.',
    canRetry: false,
  },
  /*
   * Satu-satunya kode ROLE yang dipetakan, dan sengaja: pesan generik 409 berbunyi "Periksa isian
   * Anda" — padahal yang perlu diperbaiki bukan isian melainkan KEADAAN organisasi. Permintaan
   * yang sama akan berhasil begitu ada admin kedua, dan kalimat di bawah menyebut langkah itu.
   */
  'ROLE-002': {
    title: 'Organisasi harus punya admin',
    hint: 'Ini satu-satunya admin Workforce di organisasi ini. Angkat admin lain lebih dulu, baru peran ini boleh diubah.',
    canRetry: false,
  },
  RATE_LIMITED: {
    title: 'Terlalu banyak permintaan',
    hint: 'Tunggu sebentar sebelum mencoba lagi.',
    canRetry: true,
  },
  NETWORK: {
    title: 'Tidak dapat menghubungi server',
    hint: 'Periksa koneksi Anda, lalu coba lagi.',
    canRetry: true,
  },
};

function fallbackFor(status: number): ErrorPresentation {
  /*
   * `canRetry: true` sejak Sep 2026 — sebelumnya 401 SELALU dianggap sesi benar-benar habis dan
   * cuma menawarkan Keluar. Tapi `auth.accessToken` yang dipegang layar bisa jadi salinan yang
   * baru saja disusul penyegaran di latar (dua tab/PWA berebut sesi yang sama, atau jaringan
   * sempat goyah) — pada kasus itu sesinya sendiri masih hidup, dan Coba Lagi cukup memuat ulang
   * dengan token yang sudah segar tanpa memaksa orang mengetik password lagi di tengah shift.
   * Bila sesinya memang benar-benar habis, Coba Lagi akan gagal lagi dan Keluar tetap tersedia.
   */
  if (status === 401) {
    return {
      title: 'Sesi berakhir',
      hint: 'Coba lagi dulu. Bila masih gagal, masuk kembali.',
      canRetry: true,
    };
  }
  /*
   * 403 TANPA KODE DOMAIN: JANGAN MENGARANG SEBABNYA.
   *
   * Versi sebelumnya menjawab "Akun Anda belum diberi peran di organisasi ini" — klaim SEBAB yang
   * tidak dapat didukung oleh apa pun yang diketahui di titik ini. Lebih buruk: itu sebab milik
   * `WF-404` (belum punya peran), dipakai di bawah judul milik `WF-405` (peran ada, tetapi tidak
   * mencakup tindakan). Satu kartu galat, dua diagnosis yang bertentangan.
   *
   * DITEMUKAN 24 September 2026. Pembuatan profil pekerja gagal "Tidak berhak" untuk akun yang
   * jelas ber-`WORKFORCE_ADMIN`. Petunjuk di layar mengarahkan penelusuran ke "belum diberi
   * peran" — arah yang memang tidak ada apa-apanya: `ROLE_PERMISSIONS.WORKFORCE_ADMIN` MEMUAT
   * `workforce.worker.create`, dan `TenantContextGuard` membaca peran SEGAR dari basis data pada
   * setiap permintaan, tanpa cache dan tanpa peran yang dibekukan di token.
   *
   * Kalimat di atas kartu ini sudah menampilkan pesan server apa adanya (`pesanTanpaJargon`).
   * Tugas petunjuk hanyalah menambahkan konteks bila ia PUNYA konteks. Bila tidak punya, ia harus
   * mengaku — "tidak tahu" yang jujur dapat ditelusuri, tebakan yang percaya diri tidak.
   */
  if (status === 403) {
    return {
      title: 'Tidak berhak',
      hint: 'Sebutkan tindakan yang Anda lakukan saat melapor — pesan di atas menyebut penolakannya.',
      canRetry: false,
    };
  }
  if (status === 404) {
    return { title: 'Tidak ditemukan', hint: 'Data yang diminta tidak ada.', canRetry: false };
  }
  if (status === 409 || status === 422) {
    return {
      title: 'Permintaan ditolak',
      hint: 'Periksa isian Anda; mengulang tanpa perubahan tidak akan berhasil.',
      canRetry: false,
    };
  }
  if (status === 503) {
    return {
      title: 'Layanan sedang tidak tersedia',
      hint: 'Coba beberapa saat lagi.',
      canRetry: true,
    };
  }
  return {
    title: 'Permintaan gagal',
    hint: 'Coba lagi; bila berlanjut, hubungi admin.',
    canRetry: true,
  };
}

/** Kode selalu menang atas status — kode lebih spesifik daripada angka HTTP. */
export function presentError(code: string, status: number): ErrorPresentation {
  return BY_CODE[code] ?? fallbackFor(status);
}

/** Untuk pengujian & audit: kode apa saja yang sudah punya pesan sendiri. */
export const MAPPED_ERROR_CODES = Object.freeze(Object.keys(BY_CODE));
